import { combineReducers, configureStore } from '@reduxjs/toolkit'
import {
  FLUSH,
  PAUSE,
  PERSIST,
  type PersistConfig,
  persistReducer,
  persistStore,
  PURGE,
  REGISTER,
  REHYDRATE,
} from 'redux-persist'
import storage from 'redux-persist/lib/storage'

import domainReducer from './features/domain/domainSlice'
import joinRequestReducer from './features/joinRequest/joinRequestSlice'
import memberReducer from './features/member/memberSlice'
import usageReducer from './features/usage/usageSlice'
import userReducer, { TENANT_FIELDS, type UserState } from './features/user/userSlice'
import { tenantRequestFenceMiddleware } from './tenantRequestFence'

// Pre-fix blobs under the v1 keys still contain whatever tenant the admin last
// impersonated. They're orphaned by the `-v2` key bump, so we remove them from localStorage.
try {
  localStorage.removeItem('persist:root')
  localStorage.removeItem('persist:user')
} catch {
  // SSR or storage-disabled — nothing to clean up.
}

// Tenant-scoped fields must not persist — a prior impersonation would reseed
// them on cold load and leak the wrong tenant's data. Key is bumped to `-v2`
// because redux-persist's blacklist only filters writes, so pre-fix stale
// data in existing users' `persist:user` would still rehydrate without it.
const userPersistConfig: PersistConfig<UserState> = {
  key: 'user-v2',
  storage,
  blacklist: [...TENANT_FIELDS],
}

// Nothing persists at root: `user` is wrapped above, and `usage` (previously
// whitelisted) held tenant-scoped PII. Key bumped to discard the pre-fix root
// blob that nested both.
const persistConfig = {
  key: 'root-v2',
  storage,
  whitelist: [],
}

const rootReducer = combineReducers({
  user: persistReducer(userPersistConfig, userReducer),
  domain: domainReducer,
  member: memberReducer,
  joinRequest: joinRequestReducer,
  usage: usageReducer,
})

export type RootState = ReturnType<typeof rootReducer>

const persistedReducer = persistReducer<RootState>(persistConfig, rootReducer)

export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }).concat(tenantRequestFenceMiddleware),
})

export const persistor = persistStore(store)

// Infer the `RootState` and `AppDispatch` types from the store itself
export type AppDispatch = typeof store.dispatch
